import React, { useEffect, useState } from "react";
import { Modal, Steps, Button, message, Typography, Tag} from "antd"
import { PlusOutlined } from '@ant-design/icons';

import img_status_tracker_cross from "./img/st-cross.png";
import img_status_tracker_row from "./img/st-row.png";
import img_status_tracker_col from "./img/st-col.png";
import img_cell from "./img/st-cell.png";
import img_flag from "./img/st-flag.png";
import img_acwr from "./img/st-acwr.png";
import img_filter from "./img/st-filter.png";

export const StatusTrackerTutorial = ({ setOpen, isOpen }) => {

  const [current, setCurrent] = useState(0);
  const next = () => {
    setCurrent(current + 1);
  };
  const prev = () => {
    setCurrent(current - 1);
  };


  const items = [
    {
      title: 'Intro',
      description: "Let's quickly get you up to speed.",
      content: (
        <div>
          The Status Tracker gives you a month-long snapshot of how your athletes are performing.<br />
          It brings together status, availability, workload trends, and key risk indicators all in one place.<br />

          <img style={{height: '250px', marginTop: '20px'}} src={img_status_tracker_cross} alt="status-tracker-intro"/>

        </div>
      )
    },
    {
      title: 'Table Layout',
      description: 'Rows = players. Columns = metrics. ',
      content: (
      <div>
        Each row shows a single athlete's data for the current month.<br />
        <img style={{height: '180px'}} src={img_status_tracker_row} alt="status-tracker-intro"/> <br /><br />

        
        Each column represents a specific date giving you a full team overview day by day. <br />
        <img style={{height: '180px'}} src={img_status_tracker_col} alt="status-tracker-intro"/> <br />

      </div>
        )

    },
    {
      title: 'The Cell',
      description: 'A snapshot of an athlete’s day.',
      content: (
      <div>
        <Typography.Text>
          Each cell captures everything important about an athlete on a given day:<br /><br />        
          1. A color-coded <b>status</b> — like Full, Limited, Out, etc.<br />
          2. <b>Acute-Chronic Workload Ratio (ACWR)</b> for each of your 4 key GPS metrics.<br />
          3. Flags for all your important metrics across different data streams (like GPS, Forceplate, and Testing).<br />
          4. An <b>injury marker</b> that indicates whether the athlete is injured, along with details.<br /><br />

          <img style={{height: '180px'}} src={img_cell} alt="status-tracker-cell"/>

        </Typography.Text>
        </div>)
    },
    {
      title: 'Statuses',
      description: 'Color-coded: green = good, red = bad.',
      content: (<>
        <Typography.Text>
          Player statuses are color-coded for instant clarity on an athlete's condition or availability. <br />

          You'll start with 8 defaults:
          <div>
          <Tag color="green"> Full </Tag>
          <Tag color="orange"> Limited </Tag>
          <Tag color="orange"> Modified </Tag>
          <Tag color="red"> Out </Tag>
          <Tag color="orange"> Injured </Tag>
          <Tag color="orange"> Return to Play </Tag>
          <Tag color="orange"> Rehab </Tag>
          <Tag color="orange"> Sick </Tag>
          <Tag> No Data </Tag>
          </div>
          
          <br />
          You can also create your own color-coded custom statuses to fit your team's needs by opening the cell and clicking on <Tag className='modal-tag-individual custom-tag' icon={<PlusOutlined />} >New Status</Tag>
        </Typography.Text>
      </>)
    },
    {
      title: 'ACWR Dots',
      description: 'Tiny dots, big insights.',
      content: (
        <Typography.Text>
          <p>
          The Acute-Chronic Workload Ratio (ACWR) is a measure that helps you understand the loading trends of your players.<br />
          </p>
          <p>
          Each dot represents the ACWR for a one of your 4 key metrics on that day. <br/>
          The color of the dot is determined by the ACWR value.<br />
          The colors follow a traffic light system to help you quickly assess the situation: <Typography.Text type="danger"> High</Typography.Text>, <Typography.Text type="success"> Good</Typography.Text>, <Typography.Text type="warning"> Low</Typography.Text>.
          </p>
          You can configure ACWR thresholds in <b>Team Settings → General Settings</b>.<br /><br />

          <img style={{height: '180px'}} src={img_acwr} alt="status-tracker-intro"/> <br />

         
        </Typography.Text>
      )
    },
    {
      title: 'Flags',
      description: 'Know what needs attention.',
      content: (
        <>
        <Typography.Text>
          Flags highlight outliers or risks in your athlete data — whether from GPS, Testing, or Forceplate sources.<br /><br />
          They help you quickly identify potential risks or issues that may need your attention.<br />
          These are the same flags you would see on the GPS, Testing and Forceplate Reports. <br/><br/>
          <Typography.Text type="secondary" italic>
            GPS Flags are calculated based on ACWR of your key metrics. <br/> 
            Testing and Forceplate flags are based on deviations from the athlete's historical averages.
          </Typography.Text>
        </Typography.Text>
        <br /><br />
        <img style={{height: '180px'}} src={img_flag} alt="status-tracker-intro"/> 
        </>

      )
    },
    {
      title: 'Filters',
      description: 'Zero in on what matters.',
      content: (
        <>
        <Typography.Text>
          Use the filters at the top of the page to narrow down the view to specific teams, statuses, or players.<br />
          This is useful when you want to focus on a specific group or condition without distractions.
        </Typography.Text>
        <br /><br />
        <img style={{width: '100%'}} src={img_filter} alt="status-tracker-intro"/> 
        </>
      )
    },
    {
      title: "You're Good to Go!",
      description: "That's it. Go track like a pro.",
      content: (
      <Typography.Text>
        You're all set to make the most of the Status Tracker.<br />
        Need help? Reach out to our support team anytime at <b>support@gpsdataviz.com</b>
      </Typography.Text>
      )
    },
  ];
  

  return (
  <>
  <Modal
    title="Status Tracker Tutorial"
    open={isOpen}
    footer={null}
    onCancel={() => setOpen(false)}
    width={1200}
    centered
    zIndex={9999}
  >
    <div style={{display:  'flex', justifyContent: 'left', direction:'column', gap: '20px'}}>

      <div style={{width: '30%'}}>
      <Steps 
        current={current} 
        items={items}
        direction="vertical"
        size="small"
        />
      </div>

      <div style={{ maxWidth: '70%'}}>
        <div style={{padding: '10px'}}>

          <Typography.Title level={3}>
            {items[current].title}
          </Typography.Title>

          {items[current].content}
        </div>
        <div style={{ position: 'absolute', bottom: 25, right: 25 }}>
        {current > 0 && (
            <Button style={{ margin: '0 8px' }} onClick={() => prev()}>
              Previous
            </Button>
          )}
          {current < items.length - 1 && (
            <Button type="primary" onClick={() => next()}>
              Next
            </Button>
          )}
          {current === items.length - 1 && (
            <Button type="primary" onClick={() => {setOpen(false); setCurrent(0)}}>
              Done
            </Button>
          )}
          </div>
        </div>

    </div>

  </Modal>
  </>
)

}